import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Heart, HandHeart, Handshake } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { NAV_LINKS } from "../data/content";

const involved = [
  { label: "Volunteer", to: "/get-involved?tab=volunteer" },
  { label: "Partner With Us", to: "/get-involved?tab=partner" },
  { label: "Request Support", to: "/get-involved?tab=support" },
  { label: "Contact", to: "/get-involved?tab=contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 brand-ink text-white border-b border-white/10 transition-shadow duration-300 ${
        scrolled ? "shadow-[0_10px_30px_-15px_rgba(0,0,0,0.6)]" : ""
      }`}
      data-testid="site-header"
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-2.5 md:px-8">
        <Link to="/" aria-label="Thefrent Foundation home" data-testid="nav-home-logo" className="shrink-0">
          <img src="/thefrent-logo-dark-tight.png" alt="Thefrent Foundation — Educate. Empower. Elevate." className="h-12 w-auto md:h-14" draggable={false} />
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) =>
            l.label === "Get Involved" ? (
              <div key={l.to} className="relative" onMouseEnter={() => setDdOpen(true)} onMouseLeave={() => setDdOpen(false)}>
                <NavLink
                  to={l.to}
                  data-testid="nav-link-get-involved"
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-sm font-medium transition-colors ${isActive ? "txt-gold" : "text-white/85 hover:text-white"}`
                  }
                >
                  {l.label} <ChevronDown className="h-4 w-4" />
                </NavLink>
                {ddOpen && (
                  <div className="absolute left-1/2 top-full z-50 w-48 -translate-x-1/2 pt-3">
                    <div className="overflow-hidden rounded-xl brand-ink-2 p-1.5 ring-1 ring-white/10 shadow-xl">
                      {involved.map((it) => (
                        <Link key={it.label} to={it.to} onClick={() => setDdOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-white">
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? "txt-gold" : "text-white/85 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </div>

        <div className="flex items-center gap-2.5">
          <Link to="/get-involved?tab=volunteer" data-testid="nav-volunteer" className="hidden items-center gap-2 rounded-md border border-white/25 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:border-[#c8892b] hover:text-[#e0b25a] xl:inline-flex">
            Volunteer
          </Link>
          <Link to="/donate" data-testid="nav-donate-button" className="inline-flex items-center gap-2 rounded-md brand-gold-bg px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[#0d1d47] transition-transform hover:-translate-y-0.5">
            Donate
          </Link>
          <Link to="/get-involved?tab=partner" data-testid="nav-partner" className="hidden items-center gap-2 rounded-md border border-white/25 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:border-[#c8892b] hover:text-[#e0b25a] xl:inline-flex">
            Partner
          </Link>
          <ThemeToggle className="ml-1" />
          <button
            className="grid h-10 w-10 place-items-center rounded-full border border-white/25 text-white lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="brand-ink-2 border-t border-white/10 lg:hidden" data-testid="nav-mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-3 text-base font-medium text-white/85 transition-colors hover:bg-white/5 hover:text-[#e0b25a]">
                {l.label}
              </NavLink>
            ))}
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Link to="/get-involved?tab=volunteer" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/25 px-2 py-2.5 text-xs font-semibold uppercase text-white"><Heart className="h-3.5 w-3.5" /> Volunteer</Link>
              <Link to="/donate" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-1.5 rounded-md brand-gold-bg px-2 py-2.5 text-xs font-semibold uppercase text-[#0d1d47]"><HandHeart className="h-3.5 w-3.5" /> Donate</Link>
              <Link to="/get-involved?tab=partner" onClick={() => setOpen(false)} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/25 px-2 py-2.5 text-xs font-semibold uppercase text-white"><Handshake className="h-3.5 w-3.5" /> Partner</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
